import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { botQueryOptions } from '~/queries'
import { Page } from '~/components'
import { z } from 'zod'
import { isoDateRegex } from '@bpinternal/shared'
import { LogsPanel } from '~/features/logs/components'

const yesterday = new Date(new Date().setDate(new Date().getDate() - 1)).toISOString()
const tomorrow = new Date(new Date().setDate(new Date().getDate() + 1)).toISOString()

const LogsSearchOptions = z.object({
  startDate: z
    .string()
    .regex(isoDateRegex, 'Invalid ISO date')
    .optional()
    .catch(() => yesterday),
  endDate: z
    .string()
    .regex(isoDateRegex, 'Invalid ISO date')
    .optional()
    .catch(() => tomorrow),
  conversationId: z
    .string()
    .min(1)
    .optional()
    .catch(() => undefined),
  userId: z
    .string()
    .min(1)
    .optional()
    .catch(() => undefined),
  level: z
    .enum(['INFO', 'ERROR', 'WARN', 'DEBUG'])
    .optional()
    .catch(() => undefined),
  messageContains: z
    .string()
    .min(1)
    .optional()
    .catch(() => undefined),
})

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/logs')({
  component: Component,
  validateSearch: LogsSearchOptions,
})

function Component() {
  const { workspaceId, botId } = Route.useParams()
  const bot = useSuspenseQuery(botQueryOptions({ botId, workspaceId })).data
  const { conversationId, level, userId, endDate, startDate, messageContains } = Route.useSearch()
  const navigate = Route.useNavigate()

  if (!startDate || !endDate) {
    return (
      <Navigate
        from={Route.fullPath}
        to={Route.fullPath}
        replace
        search={(prev) => ({ ...prev, startDate: startDate || yesterday, endDate: endDate || tomorrow })}
      />
    )
  }

  return (
    <Page title="Logs">
      <LogsPanel
        id={bot.id}
        type="bot"
        workspaceId={workspaceId}
        startDate={new Date(startDate)}
        setStartDate={(d: Date) =>
          navigate({
            from: Route.fullPath,
            to: Route.fullPath,
            search: (prev) => ({ ...prev, startDate: d.toISOString() }),
          })
        }
        endDate={new Date(endDate)}
        setEndDate={(d: Date) =>
          navigate({
            from: Route.fullPath,
            to: Route.fullPath,
            search: (prev) => ({ ...prev, endDate: d.toISOString() }),
          })
        }
        conversationId={conversationId}
        level={level}
        userId={userId}
        messageContains={messageContains}
        setMessageContains={(message: string | undefined) =>
          navigate({
            from: Route.fullPath,
            to: Route.fullPath,
            search: (prev) => ({ ...prev, messageContains: message }),
          })
        }
      />
    </Page>
  )
}
